import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { BlockOutlined, HourglassEmpty } from '@mui/icons-material';
import { CircularProgress, Tooltip } from '@mui/material';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import './index.scss';

import { Client, Provider, Session, SessionType } from '../../../../shared/Assets/sessionDetailsSVGs';
import { CustomAlert, CustomButton, Text } from '../../../../shared/uiComponents';
import { GREEN, ORANGE, PEARL, PRIMARY, RED, TEXT } from '../../../../shared/Helpers/colors';
import { Notes } from '../../../../shared/Assets/menuIcons';
import { errorNormalizer, formatClientName } from '../../../../shared/Helpers/functions';
import { useCheckPermission } from '../../../../shared/Helpers/hooks';
import { PERMISSIONS } from '../../../../App/constants';
import { SoapNoteStatusTypes, backgroundColors, colors } from '../../../../shared/uiComponents/StatusBadge';
import { EditSvg, ViewSvg } from '../../../../shared/Assets/svgIcons';
import { AdminTypes } from '../../../../components/Menu';
import SessionStatusIcon from '../../Session/sessionStatusIcon';
import SessionActions, { getStartSessionRefreshEventData } from './sessionActions';
import DeleteSession from './deleteSession';
import AdminActions from './adminActions';
import CancelSessionModal from './CancelSessionModal';
import DateInfo from './dateInfo';
import TimeInfo from './timeInfo';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { clearSessionInfo } from '../../../../redux/State/clientSlice/sessionSlice';
import { setInfoEvent } from '../../../../redux/State/clientSlice/calendarHelperSlice';
import { getEvents } from '../../../../redux/State/clientSlice/calendarSlice';
import { SoapNotesStatuses } from '../../../../redux/API/ClientAPIHelpers/dataCollectionProperties';
import { clearErrors, createSoapNote } from '../../../../redux/State/clientSlice/soapNoteSlice';
import { getSoapNotePDF } from '../../../../redux/State/exportSlice';
import { undoCancellation, undoCancellationGroup } from '../../../../redux/State/clientSlice/scheduleCancellationSlice';

const CancelSessionIcon = ({ color = '#1F2937' }: { color?: string }) => <BlockOutlined htmlColor={color} />;

const textColors = {
	gray: TEXT.SECONDARY,
	primary: PRIMARY.DARK[16],
	success: GREEN.DARK[16],
	warning: ORANGE.DARK[16],
	error: RED.DARK[16],
	solidGray: PEARL.BASE[700],
	solidWarning: ORANGE.BASE,
};

const CancelSessionContent: FC<{ onClose: () => void }> = ({ onClose }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const data = useSelector((state) => state.calendarHelper.infoEventData);
	const days = useSelector((state) => state.calendarHelper.displayedCalendarPageDays);
	const filterData = useSelector((state) => state.calendarHelper.filterData);
	const role = useSelector((state) => state.account.role);
	const isAdmin = role?.section.id === AdminTypes.admin;
	const canFilterEvents = isAdmin || role?.section.id === AdminTypes.cs;

	if (!data) return null;

	return (
		<CancelSessionModal
			data={data}
			onClose={onClose}
			onSuccess={() => {
				onClose();
				dispatch(getEvents(getStartSessionRefreshEventData({ canFilterEvents, days, filterData })));
			}}
		/>
	);
};

const InfoContent = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const [cancelModalOpen, setCancelModalOpen] = useState(false);
	const [undoConfirmOpen, setUndoConfirmOpen] = useState(false);
	const [undoingCancellation, setUndoingCancellation] = useState(false);

	const POST_SUBMIT_EDIT = useCheckPermission(PERMISSIONS.SOAPNOTE.POST_SUBMIT_EDIT);

	const data = useSelector((state) => state.calendarHelper.infoEventData);
	const role = useSelector((state) => state.account.role);
	const userId = useSelector((state) => state.account.user.id);
	const loading = useSelector((state) => state.export.loading);
	const isAdmin = role?.section.id === AdminTypes.admin;
	const isExpired =
		SoapNotesStatuses.expired48 === data?.reportStatus?.status ||
		SoapNotesStatuses.expiredWeek === data?.reportStatus?.status;
	const isSubmitted = SoapNotesStatuses.submitted === data?.reportStatus?.status;
	const isRecurringScheduleSession = data?.isRecurringScheduleSession;
	const requiresSoapNote = data?.sessionType?.requiresSoapNote ?? true;

	const days = useSelector((state) => state.calendarHelper.displayedCalendarPageDays);
	const filterData = useSelector((state) => state.calendarHelper.filterData);
	const canFilterEvents = isAdmin || role?.section.id === AdminTypes.cs;

	const canUserEdit = !isExpired && !isSubmitted && data?.user.id === userId;
	const canUserDoSessionActions = isAdmin || data?.user.id === userId;

	const canUserView =
		SoapNotesStatuses.pendingBT === data?.reportStatus?.status ||
		role?.section.id === AdminTypes.bcba ||
		data?.user.id === userId;
	const canAdminEditSubmitted = isAdmin && POST_SUBMIT_EDIT.permissionGranted && isSubmitted;

	const soapNoteStatusType = SoapNoteStatusTypes[data?.reportStatus?.status ?? 0];
	const iconColor = colors[soapNoteStatusType];
	const iconBackgroundColor = backgroundColors[soapNoteStatusType];
	const textColor = textColors[soapNoteStatusType];

	const navigateEditSoapNote = ({
		clientId,
		reportId,
		sessionId,
		postSubmit = false,
	}: {
		clientId: string;
		postSubmit?: boolean;
		reportId: string;
		sessionId: string;
	}) => {
		dispatch(setInfoEvent(false));
		dispatch(clearSessionInfo());
		dispatch(clearErrors());
		const query = new URLSearchParams();
		query.set('clientId', clientId);
		query.set('reportId', reportId);
		query.set('sessionId', sessionId);
		query.set('postSubmit', `${postSubmit}`);
		navigate(`/soap-notes/edit?${query.toString()}`);
	};

	const editSoapNote = () => {
		if (!data) return;
		const { client, reportId, sessionId } = data;
		if (!reportId) return;
		navigateEditSoapNote({ clientId: client.id, reportId, sessionId });
	};

	const editSubmittedSoapNote = () => {
		if (!data) return;
		const { client, reportId, sessionId } = data;
		if (!reportId) return;
		navigateEditSoapNote({
			clientId: client.id,
			reportId,
			sessionId,
			postSubmit: true,
		});
	};

	const viewSoapNoteHandler = () => {
		if (!data?.reportId) return;
		const reportId = data.reportId;
		dispatch(
			getSoapNotePDF({
				reportId,
				includeTrials: true,
			}),
		)
			.then(unwrapResult)
			.then((response) => {
				const objectURL = URL.createObjectURL(response);
				window.open(objectURL);
			})
			.catch(errorNormalizer);
	};

	const createSoapNoteHandler = () => {
		if (!data) return;

		const { client, sessionId } = data;
		dispatch(createSoapNote({ clientId: client.id, sessionId }))
			.then(unwrapResult)
			.then(({ reportId }) => {
				if (!reportId) return;
				navigateEditSoapNote({ clientId: client.id, reportId, sessionId });
			})
			.catch(errorNormalizer);
	};

	const getSoapNoteEditAction = () => {
		if (typeof data?.reportStatus?.status !== 'number') {
			return createSoapNoteHandler;
		}

		return editSoapNote;
	};

	const soapNoteAction = getSoapNoteEditAction();

	return data ? (
		<div className="infoContent">
			{data.isCancelled && (
				<div className="cancellationBanner" style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '9px', padding: '14px 16px', marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px', alignSelf: 'stretch' }}>
					<BlockOutlined style={{ color: '#DC2626', fontSize: '20px', flexShrink: 0, marginTop: '2px' }} />
					<div style={{ flex: 1 }}>
						<Text title="Session Cancelled" type="psm" fontWeight="semiBold" color="#991B1B" />
						{data.cancellationReasonName && <Text title={data.cancellationReasonName} type="pxs" color="#B91C1C" />}
						{data.cancellationNotes && <Text title={`"${data.cancellationNotes}"`} type="pxs" color="#B91C1C" style={{ fontStyle: 'italic', marginTop: '4px' }} />}
					</div>
					{isAdmin && data.cancellationId && (
						<CustomButton
							title="Restore"
							buttonType="secondary"
							onClick={() => setUndoConfirmOpen(true)}
							style={{ color: '#991B1B', borderColor: '#FECACA', backgroundColor: 'transparent', padding: '4px 12px', minWidth: 'auto', fontSize: '12px' }}
						/>
					)}
				</div>
			)}
			{data.hasPendingCancellation && !data.isCancelled && (
				<div className="pendingCancellationBanner" style={{ background: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: '9px', padding: '14px 16px', marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px', alignSelf: 'stretch' }}>
					<HourglassEmpty style={{ color: '#EA580C', fontSize: '20px', flexShrink: 0, marginTop: '2px' }} />
					<div>
						<Text title="Cancellation pending approval" type="psm" fontWeight="semiBold" color="#9A3412" />
						<Text title="This session has a pending time off request" type="pxs" color="#C2410C" />
					</div>
				</div>
			)}
			{isAdmin && (
				<div className="row">
					<div className="icon">
						<Provider />
					</div>
					<div className="rowContent">
						<div className="text">
							<Text title="Provider" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
							<Text title={data.user.fullName} type="psm" fontWeight="semiBold" />
						</div>
					</div>
				</div>
			)}
			<div className="row">
				<div className="icon">
					<Client />
				</div>
				<div className="rowContent">
					<div className="text">
						<Text title="Client" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
						<Link to={`/clients/client/${data.client.id}`} target="_blank">
							<Text title={formatClientName(data.client.fullName, data.client.isElc)} type="psm" fontWeight="semiBold" />
						</Link>
					</div>
				</div>
			</div>
			<div className="row">
				<div className="icon">
					<SessionType />
				</div>
				<div className="rowContent">
					<div className="text">
						<Text title="Session type" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
						<Text title={data.sessionType.name} type="psm" fontWeight="semiBold" />
					</div>
				</div>
			</div>
			{!isRecurringScheduleSession && (
				<>
					<div className="row">
						<div className="icon">
							<SessionStatusIcon event={data} />
						</div>
						<div className="rowContent">
							<div className="text">
								<Text title="Session Status" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
								<Text title={data.isCompleted ? 'Completed' : 'Not Completed'} type="psm" fontWeight="semiBold" />
							</div>
						</div>
					</div>
					{requiresSoapNote && <div className="row">
						<div className="icon" style={{ backgroundColor: iconBackgroundColor }}>
							<Notes color={iconColor} />
						</div>
						<div className="rowContent">
							<div className="text">
								<Text title="Soap note" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
								<Text
									title={data.reportStatus ? data.reportStatus.name : 'Not Created'}
									type="psm"
									fontWeight="semiBold"
									color={textColor}
								/>
							</div>
							{Boolean(data.sessionId) &&
								(loading ? (
									<div className="actionIcon" style={{ color: PRIMARY.DARK[16] }}>
										<CircularProgress size="16px" color="inherit" />
									</div>
								) : isAdmin ? (
									<>
										{isSubmitted && (
											<div className="actionIcon" onClick={viewSoapNoteHandler}>
												<ViewSvg />
											</div>
										)}
										{isSubmitted && canAdminEditSubmitted && !isExpired && (
											<div className="actionIcon" onClick={editSubmittedSoapNote}>
												<EditSvg />
											</div>
										)}
										{!isExpired && !isSubmitted && (
											<div className="actionIcon" onClick={soapNoteAction}>
												<EditSvg />
											</div>
										)}
									</>
								) : (
									<>
										{isSubmitted && canUserView && (
											<div className="actionIcon" onClick={viewSoapNoteHandler}>
												<ViewSvg />
											</div>
										)}
										{canUserEdit && (
											<Tooltip
												disableHoverListener={SoapNotesStatuses.pendingBT !== data?.reportStatus?.status}
												title="You cannot edit notes in Pending BT status."
											>
												<div className="actionIcon" onClick={soapNoteAction}>
													<EditSvg />
												</div>
											</Tooltip>
										)}
									</>
								))}
						</div>
					</div>}
				</>
			)}
			<DateInfo />
			<TimeInfo />
			{canUserDoSessionActions && !data.isCancelled && (
				<div className="sessionActions">
					{isRecurringScheduleSession && data.recurringScheduleId && !data.hasPendingCancellation && (
						<CustomButton
							title="Cancel Session"
							buttonType="secondary"
							onClick={() => setCancelModalOpen(true)}
							style={{ color: '#ef4444', borderColor: '#ef4444', backgroundColor: '#FEE2E2' }}
						/>
					)}
					<DeleteSession />
					<SessionActions />
					{isAdmin && !isRecurringScheduleSession && <AdminActions />}
				</div>
			)}
			{undoConfirmOpen && data && data.cancellationId && (
				<CustomAlert
					open={undoConfirmOpen}
					onClose={() => setUndoConfirmOpen(false)}
					title={data.cancellationGroupId ? 'Restore Extended Time Off' : 'Restore Session'}
					Content={() => (
						<div style={{ padding: '16px' }}>
							<Text
								title={data.cancellationGroupId
									? 'This will restore all sessions in this time-off request. Continue?'
									: 'This will restore this cancelled session. Continue?'}
								type="psm"
							/>
							<div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
								<CustomButton
									title="Cancel"
									buttonType="secondary"
									onClick={() => setUndoConfirmOpen(false)}
								/>
								<CustomButton
									title={undoingCancellation ? 'Restoring...' : 'Confirm'}
									loading={undoingCancellation}
									onClick={() => {
										if (undoingCancellation) return;
										setUndoingCancellation(true);
										const action = data.cancellationGroupId
											? dispatch(undoCancellationGroup(data.cancellationGroupId))
											: dispatch(undoCancellation(data.cancellationId!));
										action
											.then(unwrapResult)
											.then(() => {
												toast('Cancellation restored');
												setUndoConfirmOpen(false);
												dispatch(setInfoEvent(false));
												dispatch(getEvents(getStartSessionRefreshEventData({ canFilterEvents, days, filterData })));
											})
											.catch(errorNormalizer)
											.finally(() => setUndoingCancellation(false));
									}}
								/>
							</div>
						</div>
					)}
					maxWidth="xs"
				/>
			)}
			{cancelModalOpen && data && (
				<CustomAlert
					open={cancelModalOpen}
					onClose={() => {
						setCancelModalOpen(false);
						dispatch(setInfoEvent(false));
					}}
					title="Cancel Session"
					Icon={CancelSessionIcon}
					compactHeaderIcon
					iconBackgroundColor="transparent"
					maxWidth="sm"
					disableBackdropClose
					Content={CancelSessionContent}
				/>
			)}
		</div>
	) : (
		<Text title="No Information" center className="padding16" type="psm" />
	);
};

const InfoEvent = () => {
	const dispatch = useDispatch<DispatchProperties>();

	const open = useSelector((state) => state.calendarHelper.popups.info);
	const data = useSelector((state) => state.calendarHelper.infoEventData);

	const closeHandler = () => {
		dispatch(setInfoEvent(false));
	};

	return (
		<CustomAlert
			open={open}
			onClose={closeHandler}
			titleOnTop={false}
			title={data?.isRecurringScheduleSession ? 'Recurring Schedule Details' : 'Session Details'}
			Content={InfoContent}
			Icon={() => <Session />}
		/>
	);
};

export default InfoEvent;
